import { useState } from "react";
import cx from "classnames";

import HorizontalOverflow from "client/common/components/display/HorizontalOverflow";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorContainer from "client/common/components/errors/FetchErrorContainer";
import LoyaltyProgramMarketingBanners from "client/loyalty_programs/components/LoyaltyProgramMarketingBanners";
import { useCarouselDisplayQuery } from "client/products/hooks";
import CategoryCard from "client/products/components/CategoryCard";
import {
  CategoryCarouselSection,
  TopSellerCarouselSection,
} from "client/products/components/CarouselSections";
import { partitionCarousels } from "client/products/utils";
import {
  BrowseAllPatientKind,
  CategoryCarousel,
  IBrowseFilterSpeciesKind,
} from "client/products/types";
import { Clinic } from "client/clinics/types";
import { useLoyaltyProgramBannersQuery } from "client/loyalty_programs/hooks";

import CatalogFilters from "./CatalogFilters";

import styles from "../styles/HomeBrowse.module.less";

const VIEW_NAME = "Home Page";

type CategoryCarouselV2Props = {
  carousels: CategoryCarousel[];
};

export const CategoryCarouselV2 = ({ carousels }: CategoryCarouselV2Props) => {
  return (
    <HorizontalOverflow
      direction="right"
      classNames={{
        scroller: styles.categories,
      }}
    >
      {carousels.map((c) => (
        <CategoryCard.Small key={c.category.id} category={c.category} />
      ))}
    </HorizontalOverflow>
  );
};

type HomeBrowseContentProps = {
  species: IBrowseFilterSpeciesKind;
};

const HomeBrowseContent = ({ species }: HomeBrowseContentProps) => {
  const {
    data: display,
    isLoading: isBrowseLoading,
    error: browseError,
  } = useCarouselDisplayQuery({ species }, { enabled: true });
  const { data: loyaltyProgramBanners, isLoading: isLoyaltyProgramBannersLoading } =
    useLoyaltyProgramBannersQuery();

  if (isBrowseLoading || isLoyaltyProgramBannersLoading) {
    return <LoadingContainer />;
  }

  if (browseError || !display) {
    return <FetchErrorContainer error={browseError} entityName="categories" isPlural={true} />;
  }

  const { topSellerCarousel, categoryCarousels } = partitionCarousels(display.carousels);

  return (
    <div
      className={cx(
        styles.homeBrowseContent,
        loyaltyProgramBanners && loyaltyProgramBanners.length > 0 && styles.loyaltyProgramBanners,
      )}
    >
      <CategoryCarouselV2 carousels={categoryCarousels} />
      {loyaltyProgramBanners && loyaltyProgramBanners.length > 0 && (
        <LoyaltyProgramMarketingBanners banners={loyaltyProgramBanners} />
      )}
      <div className={styles.categorySections}>
        {topSellerCarousel && (
          <TopSellerCarouselSection
            productLines={topSellerCarousel.product_lines}
            viewName={VIEW_NAME}
            classNames={styles}
          />
        )}
        {categoryCarousels.map((c) => (
          <CategoryCarouselSection
            key={c.category.id}
            category={c.category}
            productLines={c.product_lines}
            viewName={VIEW_NAME}
            classNames={styles}
          />
        ))}
      </div>
    </div>
  );
};

type HomeBrowseHeaderProps = {
  species: IBrowseFilterSpeciesKind;
  setSpecies: (species: IBrowseFilterSpeciesKind) => void;
  clinic: Clinic;
};

const HomeBrowseHeader = ({ species, setSpecies, clinic }: HomeBrowseHeaderProps) => {
  return (
    <div className={styles.heading}>
      <div className={styles.header}>
        Search by our <span className={styles.accent}>popular categories</span>
      </div>
      <CatalogFilters species={species} setSpecies={setSpecies} clinic={clinic} />
    </div>
  );
};

const HomeBrowseV2 = ({ clinic }: { clinic: Clinic }) => {
  const [species, setSpecies] = useState<IBrowseFilterSpeciesKind>(BrowseAllPatientKind.ALL);
  return (
    <div className={styles.homeBrowse}>
      <HomeBrowseHeader species={species} setSpecies={setSpecies} clinic={clinic} />
      <HomeBrowseContent species={species} />
    </div>
  );
};

export default HomeBrowseV2;
